/**
 * Notification bell — top-bar affordance for the in-app feed.
 *
 * Polls /v1/notifications/bundle every 60s while mounted. Re-fetches
 * eagerly on tab focus so a user returning from another tab sees fresh
 * state. Click → dropdown with the last 10; click an item → navigate to
 * its target_url AND mark it read. "Mark all as read" + "View all"
 * affordances at the bottom of the dropdown.
 *
 * Only renders for signed-in users (wrapped at the Topbar level).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, FileText, Vote, Calendar, Users, Mic2 } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useT } from '../i18n';
import { Tooltip } from './ui/Tooltip';
import { useAppContext } from '../context/AppContext';
import type { UILang } from '../context/AppContext';
import { formatDateAbbrMonth } from '../lib/format';
import {
  fetchNotificationBundle,
  markAllNotificationsRead,
  markNotificationRead,
  type NotificationItem,
  type NotificationKind,
} from '../lib/notificationsApi';


const POLL_INTERVAL_MS = 60_000;


/** Maps each notification `kind` to its icon. Keeps the dropdown rows
 *  visually scannable without relying on prose alone. */
function KindIcon({ kind }: { kind: NotificationKind }) {
  const cls = 'text-[var(--text-2)]';
  switch (kind) {
    case 'vote_result':      return <Vote     size={14} className={cls} />;
    case 'bill_status':      return <FileText size={14} className={cls} />;
    case 'session_reminder': return <Calendar size={14} className={cls} />;
    case 'member_vote':      return <Users    size={14} className={cls} />;
    case 'member_speech':    return <Mic2     size={14} className={cls} />;
    default:                 return <FileText size={14} className={cls} />;
  }
}


/** One row in the dropdown. Builds the message string per-kind from the
 *  notification payload — the backend stores the structured payload, the
 *  frontend localises the prose. */
function formatMessage(t: ReturnType<typeof useT>, n: NotificationItem): string {
  const p = n.payload as Record<string, string | number | undefined>;
  const affair = p.affair_number ?? '';
  const title  = p.title ?? '';
  switch (n.kind) {
    case 'vote_result':
      return t('notif.row.voteResult')
        .replace('{affair}', String(affair))
        .replace('{title}',  String(title));
    case 'bill_status':
      return t('notif.row.billStatus')
        .replace('{affair}', String(affair))
        .replace('{title}',  String(title))
        .replace('{status}', String(p.new_status ?? ''));
    case 'session_reminder':
      return t('notif.row.sessionReminder')
        .replace('{name}', String(p.session_name ?? ''));
    case 'member_vote':
      return t('notif.row.memberVote')
        .replace('{member}',   String(p.councillor_name ?? ''))
        .replace('{decision}', String(p.decision ?? ''))
        .replace('{affair}',   String(p.affair_number ?? ''))
        .replace('{title}',    String(p.bill_title ?? ''));
    case 'member_speech':
      return t('notif.row.memberSpeech')
        .replace('{member}', String(p.councillor_name ?? ''))
        .replace('{affair}', String(p.affair_number ?? ''))
        .replace('{title}',  String(p.bill_title ?? ''));
    default:
      return title ? String(title) : t('notif.row.generic');
  }
}


/** Compact 'time ago' formatter — keeps the dropdown clean (no full date
 *  strings). Localised relative-time shorthand; falls back to an absolute
 *  date once the notification is older than ~30 days. */
function timeAgo(t: ReturnType<typeof useT>, uiLang: UILang, iso: string | null): string {
  if (!iso) return '';
  const now = Date.now();
  const then = new Date(iso).getTime();
  const sec = Math.max(0, Math.floor((now - then) / 1000));
  if (sec < 60)        return t('notif.ago.seconds', '{n}s ago').replace('{n}', String(sec));
  if (sec < 3600)      return t('notif.ago.minutes', '{n}m ago').replace('{n}', String(Math.floor(sec / 60)));
  if (sec < 86_400)    return t('notif.ago.hours',   '{n}h ago').replace('{n}', String(Math.floor(sec / 3600)));
  if (sec < 86_400*30) return t('notif.ago.days',    '{n}d ago').replace('{n}', String(Math.floor(sec / 86_400)));
  return formatDateAbbrMonth(iso, uiLang);
}


export function NotificationBell() {
  const t = useT();
  const { uiLang } = useAppContext();
  const navigate = useNavigate();
  const { user, getAccessToken } = useAuth();
  const [open,   setOpen]   = useState(false);
  const [items,  setItems]  = useState<NotificationItem[]>([]);
  const [unread, setUnread] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  const refresh = useCallback(async () => {
    if (!user?.id) return;
    try {
      const bundle = await fetchNotificationBundle(getAccessToken, 10);
      setItems(bundle.items);
      setUnread(bundle.unread);
    } catch {
      // Silent — bell is a non-critical surface. A failed poll just leaves
      // the previous state on screen; next interval will retry.
    }
  }, [user?.id, getAccessToken]);

  // Initial fetch + polling
  useEffect(() => {
    if (!user?.id) return;
    refresh();
    const id = window.setInterval(refresh, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [user?.id, refresh]);

  // Refresh on tab focus so a user returning from another tab sees fresh state
  useEffect(() => {
    if (!user?.id) return;
    const onFocus = () => { refresh(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [user?.id, refresh]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleItemClick = async (n: NotificationItem) => {
    setOpen(false);
    if (!n.read_at) {
      // Optimistic: flip the local state immediately so the badge updates
      // without waiting for the network round-trip.
      setItems(prev => prev.map(x => x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x));
      setUnread(prev => Math.max(0, prev - 1));
      markNotificationRead(getAccessToken, n.id).catch(() => {
        // Rollback on failure — re-add to unread set
        setItems(prev => prev.map(x => x.id === n.id ? { ...x, read_at: null } : x));
        setUnread(prev => prev + 1);
      });
    }
    if (n.target_url) navigate(n.target_url);
  };

  const handleMarkAll = async () => {
    // Optimistic
    setItems(prev => prev.map(x => ({ ...x, read_at: x.read_at ?? new Date().toISOString() })));
    setUnread(0);
    markAllNotificationsRead(getAccessToken).catch(() => {
      // Soft rollback — re-fetch ground truth
      refresh();
    });
  };

  // Hide the whole component for signed-out users (Topbar also wraps in
  // <Show when="signed-in"> but defensive here too).
  if (!user?.id) return null;

  return (
    <div className="relative" ref={rootRef}>
      <Tooltip content={unread > 0 ? `${unread} ${t('notif.bell.unread')}` : t('notif.bell.aria')} width={160} className="relative inline-flex">
        <button
          onClick={() => setOpen(o => !o)}
          aria-label={t('notif.bell.aria')}
          className="relative w-[32px] h-[32px] flex items-center justify-center rounded-[var(--r1)] text-[var(--text-2)] hover:bg-[var(--surface-2)] hover:text-[var(--text-1)] transition-colors"
        >
          <Bell size={18} />
          {unread > 0 && (
            <span
              className="absolute top-[2px] right-[2px] min-w-[16px] h-[16px] px-[3px] flex items-center justify-center text-[11px] font-semibold leading-none text-white bg-[#CC0000] rounded-full"
              aria-hidden
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </Tooltip>

      {open && (
        <div
          className="absolute right-0 top-[calc(100%+6px)] w-[360px] max-h-[480px] bg-[var(--surface)] border border-[var(--border)] rounded-[var(--r2)] shadow-xl z-50 flex flex-col"
          role="dialog"
          aria-label={t('notif.dropdown.title')}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-[12px] py-[8px] border-b border-[var(--border)]">
            <span className="text-[12px] font-semibold text-[var(--text-1)]">
              {t('notif.dropdown.title')}
            </span>
            {unread > 0 && (
              <button
                onClick={handleMarkAll}
                className="flex items-center gap-[4px] text-[11px] text-[var(--text-3)] hover:text-[var(--accent)] transition-colors"
              >
                <Check size={12} /> {t('notif.dropdown.markAllRead')}
              </button>
            )}
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto custom-scrollbar">
            {items.length === 0 ? (
              <div className="px-[16px] py-[24px] text-center text-[12px] text-[var(--text-3)]">
                {t('notif.dropdown.empty')}
              </div>
            ) : (
              items.map(n => (
                <button
                  key={n.id}
                  onClick={() => handleItemClick(n)}
                  className={`w-full flex items-start gap-[10px] px-[12px] py-[10px] text-left border-b border-[var(--border)] last:border-b-0 hover:bg-[var(--surface-2)] transition-colors ${
                    n.read_at ? 'opacity-60' : ''
                  }`}
                >
                  <span className="mt-[2px] shrink-0"><KindIcon kind={n.kind} /></span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] text-[var(--text-1)] leading-snug line-clamp-2">
                      {formatMessage(t, n)}
                    </p>
                    <p className="text-[11px] text-[var(--text-3)] mt-[2px]">
                      {timeAgo(t, uiLang, n.created_at)}
                    </p>
                  </div>
                  {!n.read_at && (
                    <span className="mt-[5px] w-[6px] h-[6px] rounded-full bg-[var(--accent)] shrink-0" aria-label={t('notif.unread', 'unread')} />
                  )}
                </button>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="px-[12px] py-[8px] border-t border-[var(--border)]">
            <button
              onClick={() => { setOpen(false); navigate('/notifications'); }}
              className="w-full text-[11px] text-[var(--text-2)] hover:text-[var(--accent)] transition-colors"
            >
              {t('notif.dropdown.viewAll')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
